import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { StyledHeader } from './style';
import { Link, useLocation } from 'react-router-dom';
import { SITES_MAP_PATH, TENANT_SWITCH_PATH } from 'src/routes/config';
import { useMapFilters } from 'src/contexts/siteMapFilterContext';
import { MapSearch } from 'src/features/sites/components/map-search';
import { useSite } from 'src/contexts/siteContext';
import { UserPopoverBtn } from 'src/components';
import { ExtendedHeader } from '../extendedHeader';
import { ExtendedNavItemTypes } from '../extendedHeader/types';

import SiteHeaderWrapper from 'src/features/sites/components/site-header/SiteHeaderWrapper';
import { useCallback } from 'react';
import { useResponsive } from 'src/hooks/useResponsive';
import { ListIcon } from 'src/assets/icons/ListIcon';
type HeaderProps = {
  title?: string;
  showButton?: boolean;
  isTransparent?: boolean;
  onTitleChange?: (title: string) => void;
  isTitleEditable?: boolean;
  extendedHeaderTitle?: string;
  isSiteHeader?: boolean;
  isFullscreen?: boolean;
  activeExtendedNavItem?: string;
  isExtendedHeader?: boolean;
  extendedNavItems?: ExtendedNavItemTypes[];
  defaultExtendedHeaderTitle?: string;
  onNavigateBackClick?: () => void;
};

export const Header = ({
  title,
  showButton = false,
  onTitleChange,
  isSiteHeader = false,
  isFullscreen = false,
  activeExtendedNavItem,
  onNavigateBackClick,
  isTransparent = false,
  extendedHeaderTitle,
  isTitleEditable = true,
  isExtendedHeader = false,
  extendedNavItems,
  defaultExtendedHeaderTitle,
}: HeaderProps) => {
  const theme = useTheme();

  const { getSite } = useSite();
  const site = getSite();
  const siteExists = site && Object.values(site).length;
  const isMobile = useResponsive('down', 'sm');

  const location = useLocation();
  // On mobile/tablet the SiteMap page renders its own MapSearch overlay, so the
  // header only hosts it on desktop (matching AppLayout's header breakpoint).
  const isMobileOrTablet = useResponsive('down', 'md');
  const showMapSearch = location.pathname === SITES_MAP_PATH && !isMobileOrTablet;
  const onTitleChangeCallback = useCallback(
    (value: any) => {
      onTitleChange && onTitleChange(value);
    },
    [onTitleChange]
  );

  const { updateMapFilters, mapRef, markersRef, sites, setSites, isSitesLoaded } = useMapFilters();

  const handleAccountSwitch = () => {
    updateMapFilters('');
  };

  if (siteExists && isSiteHeader) {
    return <SiteHeaderWrapper site={site} isFullscreen={isFullscreen} />;
  }

  if (isExtendedHeader) {
    return (
      <ExtendedHeader
        onNavigateBackClick={onNavigateBackClick}
        extendedHeaderTitle={extendedHeaderTitle}
        defaultExtendedHeaderTitle={defaultExtendedHeaderTitle}
        activeExtendedNavItem={activeExtendedNavItem}
        key="header"
        isTitleEditable={isTitleEditable}
        onTitleChange={onTitleChangeCallback}
        isMobile={isMobile}
        isTransparent={isTransparent}
        extendedNavItems={extendedNavItems}
      />
    );
  }

  return (
    <StyledHeader
      isTransparent={isTransparent}
      component="header"
      data-sm="layout:header"
      theme={theme}
      isMobile={isMobile}
    >
      <Stack
        flexDirection={'row'}
        justifyContent={title || showMapSearch ? 'space-between' : 'flex-end'}
        alignItems={'center'}
        sx={{ width: '100%' }}
      >
        {showMapSearch ? (
          <Box sx={{ zIndex: 1300, width: '100%' }}>
            <MapSearch
              siteData={sites}
              markersRef={markersRef}
              mapRef={mapRef}
              setSites={setSites}
              isLoaded={isSitesLoaded}
              inHeader
            />
          </Box>
        ) : title ? (
          <Box component="div">
            <Typography variant="h4" component="h1" data-sm="title">
              {title}
            </Typography>
          </Box>
        ) : null}
        <Stack flexDirection={'row'} flexShrink={0}>
          {showButton &&
            (isMobile ? (
              <IconButton to={TENANT_SWITCH_PATH} component={Link} onClick={handleAccountSwitch}>
                <ListIcon width={24} height={24} />
              </IconButton>
            ) : (
              <Button
                variant="outlined"
                startIcon={<ListIcon width={18} height={18} />}
                sx={{ mr: '40px', flexShrink: 0, whiteSpace: 'nowrap' }}
                to={TENANT_SWITCH_PATH}
                component={Link}
                onClick={handleAccountSwitch}
              >
                Switch Account
              </Button>
            ))}
          <UserPopoverBtn isTransparent={isTransparent} />
        </Stack>
      </Stack>
    </StyledHeader>
  );
};
